import i18next from "../../i18n";
import { OffersToAccept, OrderResponse } from "../../types";
import { FormattedOffer } from "./awardFormat";

export const actions = {
  CLEAR_OFFERS: "CLEAR_OFFERS",
  UPDATE_VOLUME: "UPDATE_VOLUME",
  UPDATE_PRICE: "UPDATE_PRICE",
  DELETE_OFFER: "DELETE_OFFER",
  UPDATE_ON_CHECKBOX_CHANGE: "UPDATE_ON_CHECKBOX_CHANGE",
  SELECT_BEST_OFFERS: "SELECT_BEST_OFFERS",
  /** Pre-select exactly the offers the offer assistant recommended (`action.offerIds`). */
  APPLY_RECOMMENDATION: "APPLY_RECOMMENDATION",
};

export type EventState = {
  offersToAccept: OffersToAccept[];
  errors: any[];
  invalid: any[];
};

export const initialEventState: EventState = {
  offersToAccept: [],
  errors: [],
  invalid: [],
};

/**
 * Single-order award reducer, factored out of `Award.tsx` verbatim. The reducer closes over
 * the current `order` (for the requested-quantity ceiling) and `formattedOffers` (for
 * best-offer selection), so it is rebuilt on each render exactly as the inline version was.
 */
export const createAwardReducer =
  (order: OrderResponse, formattedOffers: FormattedOffer[]) =>
  (state: EventState, action: any): EventState => {
    let newState = { ...state };
    switch (action.type) {
      case actions.UPDATE_ON_CHECKBOX_CHANGE:
        {
          const offerIndex = newState.offersToAccept.findIndex(
            (offer) => offer.id === action.id
          );
          if (offerIndex >= 0) {
            newState.offersToAccept[offerIndex] = {
              ...newState.offersToAccept[offerIndex],
              volume: action.volume,
              price: action.price,
            };
          } else {
            newState.offersToAccept.push({
              id: action.id,
              volume: action.volume,
              price: action.price,
              orderId: action.orderId,
              material: action.material,
              offer: action.offer,
            });
          }
        }
        break;
      case actions.UPDATE_VOLUME:
        {
          const offerIndex = newState.offersToAccept.findIndex(
            (offer) => offer.id === action.id
          );
          if (offerIndex >= 0) {
            newState.offersToAccept[offerIndex] = {
              ...newState.offersToAccept[offerIndex],
              volume: action.volume,
            };
          } else {
            newState.offersToAccept.push({
              id: action.id,
              volume: action.volume,
              orderId: action.orderId,
            } as OffersToAccept);
          }
        }
        break;
      case actions.UPDATE_PRICE:
        {
          const offerIndex = newState.offersToAccept.findIndex(
            (offer) => offer.id === action.id
          );
          if (offerIndex >= 0) {
            newState.offersToAccept[offerIndex] = {
              ...newState.offersToAccept[offerIndex],
              price: action.price,
            };
          } else {
            newState.offersToAccept.push({
              id: action.id,
              price: action.price,
              orderId: action.orderId,
            } as OffersToAccept);
          }
        }
        break;
      case actions.SELECT_BEST_OFFERS:
        {
          const bestOffers: any = [];
          newState.offersToAccept = [];
          formattedOffers.forEach((offer) => {
            if (offer.isBest) {
              bestOffers.push({
                id: offer.id,
                volume: offer.order.quantity,
                price: offer.price,
                orderId: offer.order.id,
                material: offer.order.material.globalMaterialName,
                offer,
              });
            }
          });
          newState.offersToAccept = bestOffers;
        }
        break;
      case actions.APPLY_RECOMMENDATION:
        {
          // Replace the whole selection with the recommended offers, same entry shape as
          // SELECT_BEST_OFFERS. Ids that are not among the current offers are ignored. The
          // validation lists are rebuilt from scratch so no stale error survives the swap.
          const ids = new Set<string>(action.offerIds ?? []);
          // `material` carries the material name here, exactly as SELECT_BEST_OFFERS stores it.
          const recommended: any[] = formattedOffers
            .filter((offer) => ids.has(offer.id))
            .map((offer) => ({
              id: offer.id,
              volume: offer.order.quantity,
              price: offer.price,
              orderId: offer.order.id,
              material: offer.order.material.globalMaterialName,
              offer,
            }));
          newState = { offersToAccept: recommended, errors: [], invalid: [] };
        }
        break;
      case actions.DELETE_OFFER:
        newState = {
          ...newState,
          offersToAccept: state.offersToAccept.filter(
            (offer) => offer.id !== action.id
          ),
        };
        break;
    }

    const volumeSums: any = {};
    newState.offersToAccept.forEach((offer) => {
      const isInvalidVolume = offer.volume <= 0;
      const isInvalidPrice = offer.price <= 0;
      const errorIndex = newState.invalid.findIndex(
        (error) => error.id === offer.id
      );

      if (isInvalidVolume || isInvalidPrice) {
        const errorMessage = i18next.t("tenders.errors.invalid");

        if (errorIndex === -1) {
          newState.invalid.push({ id: offer.id, message: errorMessage });
        } else {
          newState.invalid[errorIndex] = {
            id: offer.id,
            message: errorMessage,
          };
        }
      } else if (errorIndex !== -1) {
        // If previously had an error and now corrected, remove the error
        newState.invalid.splice(errorIndex, 1);
      }

      volumeSums[offer.orderId] =
        (volumeSums[offer.orderId] || 0) +
        parseFloat(String(offer.volume || 0));
    });

    Object.keys(volumeSums).forEach((orderId) => {
      const totalVolume = volumeSums[orderId];
      const requestedVolume = order.quantity;
      const errorIndex = newState.errors.findIndex(
        (error) => error.orderId === orderId
      );

      if (totalVolume > requestedVolume) {
        const material = newState.offersToAccept.find(
          (o) => o.orderId === orderId
        )?.material;
        const errorMessage = `${i18next.t(
          "tenders.errors.message"
        )} ${totalVolume} > ${requestedVolume}`;

        if (errorIndex === -1) {
          newState.errors.push({ orderId, message: errorMessage, material });
        } else {
          newState.errors[errorIndex] = {
            orderId,
            material,
            message: errorMessage,
          };
        }
      } else if (errorIndex !== -1) {
        newState.errors.splice(errorIndex, 1);
      }
    });

    return newState;
  };
